import { useEffect, useState } from 'react';
import { sizeGuidesApi } from '@/lib/api/arrex/catalog';
import { useApp } from '@/contexts/AppContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, ImageIcon } from 'lucide-react';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { imgUrl } from '@/lib/format';
import { readItems } from '@/lib/api/normalize';
import { CardGridSkeleton } from '@/components/ui/skeletons';

const emptyForm = { name_en: '', name_ar: '', image_url: '' };

export default function AdminSizeGuides() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const [items, setItems] = useState<any[] | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => sizeGuidesApi.list().then((res) => setItems(readItems(res)));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(emptyForm); setOpen(true); };
  const startEdit = (g: any) => { setForm({ name_en: g.name_en, name_ar: g.name_ar || '', image_url: g.image_url || '', id: g.id }); setOpen(true); };

  const save = async () => {
    if (!form.name_en) return toast.error('Name (EN) required');
    setSaving(true);
    // Send empty rows array — all guide info is in the image
    const payload = {
      nameEn: form.name_en,
      nameAr: form.name_ar || form.name_en,
      rows: [],
      imageUrl: form.image_url || null,
    };
    try {
      if (form.id) await sizeGuidesApi.update(form.id, payload);
      else await sizeGuidesApi.create(payload);
      toast.success(form.id ? 'Updated' : 'Created');
      setOpen(false);
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await sizeGuidesApi.remove(deleteTarget.id);
      toast.success('Deleted');
      setDeleteTarget(null);
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="font-display text-3xl font-bold">{t('admin.nav.sizeGuides')}</h1>
        <Button onClick={startNew}><Plus className="w-4 h-4 me-1" />New</Button>
      </div>

      {items === null ? (
        <CardGridSkeleton count={3} />
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">No size guides yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((g) => (
            <div key={g.id} className="surface-card rounded-lg p-3 flex items-center gap-3">
              {/* Small square thumbnail */}
              {g.image_url ? (
                <img
                  src={imgUrl(g.image_url)}
                  alt={lang === 'ar' ? g.name_ar : g.name_en}
                  className="w-14 h-14 rounded object-cover shrink-0 border border-border"
                />
              ) : (
                <div className="w-14 h-14 rounded bg-muted flex items-center justify-center shrink-0 border border-border">
                  <ImageIcon className="w-5 h-5 text-muted-foreground/40" />
                </div>
              )}

              {/* Name + actions */}
              <p className="font-semibold text-sm truncate flex-1">
                {lang === 'ar' ? (g.name_ar || g.name_en) : g.name_en}
              </p>
              <div className="flex gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => startEdit(g)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(g)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit' : 'New'} Size Guide</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Names */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('admin.common.nameEn')}</Label>
                <Input
                  value={form.name_en}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="e.g. T-Shirt Guide"
                />
              </div>
              <div>
                <Label>{t('admin.common.nameAr')}</Label>
                <Input
                  value={form.name_ar}
                  onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                  placeholder="اسم دليل المقاسات"
                  dir="rtl"
                />
              </div>
            </div>

            {/* Image — the whole guide is here */}
            <div>
              <Label>{t('admin.sizeGuidesPage.imageLabel')}</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Upload the size guide chart image — all measurements should be visible in the image.
              </p>
              <ImageUploader
                value={form.image_url || null}
                onChange={(url) => setForm({ ...form, image_url: url || '' })}
                bucket="product-images"
                pathPrefix="sizeguides/"
                aspect="square"
                previewClassName="w-full h-64"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('admin.common.cancel')}</Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 me-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              {t('admin.deleteSizeGuide')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && t('admin.deleteSizeGuideConfirm', {
                name: lang === 'ar' ? deleteTarget.name_ar : deleteTarget.name_en,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('admin.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
